import { useMemo, useState } from "react";
import { CheckCircle2, FlaskConical, History, Play, RefreshCw, Save, XCircle } from "lucide-react";
import { formatDateTime, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, EmptyState, Field, InlineAlert, Input, NativeSelect, Skeleton, Switch, Tabs, TabsContent, TabsList, TabsTrigger, cn, notifyDone, toast } from "@ncp/ui";
import { isReadOnly, useConnectorActions, useConnectorCalls, type ConnectorBody, type ConnectorCallResult, type ConnectorDefinition, type ConnectorOptions } from "./api";

const cell = (v: unknown) => (v == null ? "" : typeof v === "object" ? JSON.stringify(v) : String(v));

/** Calls one operation of this exact version (drafts included) through the runtime: real authentication, retries, breaker and call log. */
export function TestPanel({ definition, body, dirty, onSave, options }: { definition: ConnectorDefinition; body: ConnectorBody; dirty: boolean; onSave: () => Promise<void>; options?: ConnectorOptions }) {
  const { t } = useTranslation();
  const actions = useConnectorActions();
  const [operationKey, setOperationKey] = useState(body.operations[0]?.key ?? "");
  const [inputs, setInputs] = useState<Record<string, Record<string, string>>>({});
  const [environment, setEnvironment] = useState("");
  const [result, setResult] = useState<ConnectorCallResult | null>(null);
  const op = body.operations.find((o) => o.key === operationKey) ?? body.operations[0];
  const values = inputs[op?.key ?? ""] ?? {};
  const missing = (op?.parameters ?? []).filter((p) => p.required && !p.default && !values[p.name]?.trim()).map((p) => p.label ?? p.name);

  const run = async () => {
    if (!op) return;
    // A failed save (reported by the save) stops here, so the old version is not tested instead.
    if (dirty) { try { await onSave(); } catch { return; } }
    try {
      const input = Object.fromEntries(Object.entries(values).filter(([, v]) => v.trim()));
      const { call: r } = await actions.test.mutateAsync({ id: definition.id, operation: op.key, input, environment: environment || null });
      setResult(r);
      const text = t("admin:connectors.test.result", { status: r.statusCode || "—", ms: r.latencyMs });
      if (r.succeeded) notifyDone(text); else toast.warning(text);
    } catch { /* useSave reported the failure with its cause */ }
  };

  if (body.operations.length === 0) return <EmptyState icon={FlaskConical} title={t("admin:connectors.test.noOperations")} description={t("admin:connectors.test.noOperationsBody")} />;
  return (
    <div className="grid gap-4 xl:grid-cols-[380px_minmax(0,1fr)]">
      <Card className="self-start">
        <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-base"><FlaskConical className="size-4 text-aegold-700" /> {t("admin:connectors.test.title")}</CardTitle><CardDescription>{t("admin:connectors.test.hint", { version: definition.version })}</CardDescription></CardHeader>
        <CardContent className="space-y-3">
          <Field label={t("admin:connectors.test.operation")}>
            <NativeSelect value={op?.key ?? ""} onChange={(e) => { setOperationKey(e.target.value); setResult(null); }}>
              {body.operations.map((o) => <option key={o.key} value={o.key}>{o.method} · {o.name || o.key}</option>)}
            </NativeSelect>
          </Field>
          {(op?.parameters ?? []).map((p) => (
            <Field key={p.name} label={`${p.label ?? p.name} (${p.name})`} required={p.required && !p.default} hint={p.description ?? undefined}>
              <Input value={values[p.name] ?? ""} dir="ltr" className="font-mono text-xs" placeholder={p.default ?? p.example ?? ""}
                onChange={(e) => setInputs({ ...inputs, [op!.key]: { ...values, [p.name]: e.target.value } })} />
            </Field>
          ))}
          <Field label={t("admin:connectors.test.environment")} hint={t("admin:connectors.test.environmentHint", { env: options?.platformEnvironment ?? "" })}>
            <NativeSelect value={environment} onChange={(e) => setEnvironment(e.target.value)}>
              <option value="">{t("admin:connectors.test.platformEnvironment", { env: options?.platformEnvironment ?? "" })}</option>
              {(options?.environments ?? []).filter((e) => e !== options?.platformEnvironment).map((e) => <option key={e} value={e}>{e}{body.environmentOverrides[e] ? ` · ${t("admin:connectors.env.overridden")}` : ""}</option>)}
            </NativeSelect>
          </Field>
          {op && !isReadOnly(op) ? <InlineAlert tone="warning">{t("admin:connectors.test.writeWarning")}</InlineAlert> : null}
          {dirty ? <InlineAlert tone="info">{t("admin:connectors.test.saveFirst")}</InlineAlert> : null}
          <Button variant="gold" className="w-full" disabled={!op || missing.length > 0} loading={actions.test.isPending} onClick={run}>{dirty ? <Save /> : <Play />} {dirty ? t("admin:connectors.test.saveAndRun") : t("admin:connectors.test.run")}</Button>
          {missing.length ? <p className="text-[11px] text-danger-700">{t("admin:connectors.test.missing", { names: missing.join(", ") })}</p> : null}
        </CardContent>
      </Card>
      {result ? <ResultView result={result} /> : <Card className="grid min-h-[320px] place-items-center p-6 text-center text-sm text-muted-foreground">{t("admin:connectors.test.empty")}</Card>}
    </div>
  );
}

function ResultView({ result }: { result: ConnectorCallResult }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const columns = result.columns.length ? result.columns : Object.keys(result.rows[0] ?? {}).map((name) => ({ name, label: name, path: `$.${name}` }));
  const fields = Object.entries(result.fields ?? {});
  const raw = useMemo(() => {
    if (!result.rawResponse) return "";
    try { return JSON.stringify(JSON.parse(result.rawResponse), null, 2); } catch { return result.rawResponse; }
  }, [result.rawResponse]);
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center gap-2">
          {result.succeeded ? <Badge variant="success" dot><CheckCircle2 /> {t("admin:connectors.test.ok")}</Badge> : <Badge variant="danger" dot><XCircle /> {t("admin:connectors.test.failed")}</Badge>}
          <Badge variant="outline" className="tabular">HTTP {result.statusCode || "—"}</Badge>
          <Badge variant="outline" className="tabular">{result.latencyMs} ms</Badge>
          <Badge variant="outline">{t("admin:connectors.test.attempts", { count: result.attempts })}</Badge>
          <Badge variant="outline">{result.environment}</Badge>
          <Badge variant="default">v{result.version}</Badge>
          <span className="ms-auto text-xs text-muted-foreground">{formatDateTime(result.at, locale)}</span>
        </div>
        <p className="mt-2 break-all font-mono text-[11px] text-muted-foreground" dir="ltr">{result.method} {result.url}</p>
        {result.error ? <InlineAlert tone="danger" className="mt-2">{result.error}</InlineAlert> : null}
      </CardHeader>
      <CardContent>
        <Tabs defaultValue={result.rows.length ? "table" : fields.length ? "fields" : "raw"}>
          <TabsList>
            <TabsTrigger value="table">{t("admin:connectors.test.table", { count: result.rows.length })}</TabsTrigger>
            <TabsTrigger value="fields">{t("admin:connectors.test.fields", { count: fields.length })}</TabsTrigger>
            <TabsTrigger value="request">{t("admin:connectors.test.request")}</TabsTrigger>
            <TabsTrigger value="raw">{t("admin:connectors.test.raw")}</TabsTrigger>
          </TabsList>
          <TabsContent value="table">
            {result.rows.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">{t("admin:connectors.test.noRows")}</p> : (
              <div className="max-h-[480px] overflow-auto rounded-lg border border-sand-200">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-sand-50 text-[10px] uppercase tracking-wider text-sand-700"><tr className="[&>th]:px-2 [&>th]:py-2 [&>th]:text-start">{columns.map((c) => <th key={c.name}>{c.label ?? c.name}</th>)}</tr></thead>
                  <tbody>{result.rows.map((r, i) => <tr key={i} className="border-t border-sand-100 [&>td]:px-2 [&>td]:py-1.5">{columns.map((c) => <td key={c.name} className={cn(typeof r[c.name] === "number" && "tabular text-end")}>{cell(r[c.name])}</td>)}</tr>)}</tbody>
                </table>
              </div>
            )}
          </TabsContent>
          <TabsContent value="fields">
            {fields.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">{t("admin:connectors.test.noFields")}</p> : (
              <dl className="grid gap-x-4 gap-y-1.5 text-sm sm:grid-cols-[200px_minmax(0,1fr)]">{fields.map(([k, v]) => <div key={k} className="contents"><dt className="font-mono text-xs text-muted-foreground">{k}</dt><dd className="break-words">{cell(v)}</dd></div>)}</dl>
            )}
          </TabsContent>
          <TabsContent value="request">
            <div className="space-y-2 text-xs">
              <p className="font-mono" dir="ltr">{result.method} {result.url}</p>
              <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-[200px_minmax(0,1fr)]">{Object.entries(result.requestHeaders ?? {}).map(([k, v]) => <div key={k} className="contents"><dt className="font-mono text-muted-foreground">{k}</dt><dd className="break-all font-mono" dir="ltr">{v}</dd></div>)}</dl>
              {result.requestBody ? <pre className="max-h-64 overflow-auto rounded-lg bg-sand-50 p-3 font-mono text-[11px]" dir="ltr">{result.requestBody}</pre> : null}
              <p className="text-[11px] text-muted-foreground">{t("admin:connectors.test.masked")}</p>
            </div>
          </TabsContent>
          <TabsContent value="raw">
            {raw ? <pre className="max-h-[480px] overflow-auto rounded-lg bg-aeblack-950 p-3 font-mono text-[11px] text-sand-100" dir="ltr">{raw}</pre> : <p className="py-6 text-center text-sm text-muted-foreground">—</p>}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

/** The connector's call log: every call the runtime made (tests, agents, assistants, automations), newest first. */
export function CallsPanel({ connectorKey }: { connectorKey: string }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [live, setLive] = useState(false);
  const [failedOnly, setFailedOnly] = useState(false);
  const calls = useConnectorCalls(connectorKey, live);
  const rows = (calls.data ?? []).filter((c) => !failedOnly || !c.succeeded);
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div><CardTitle className="flex items-center gap-2 text-base"><History className="size-4 text-aegold-700" /> {t("admin:connectors.calls.title")}</CardTitle><CardDescription>{t("admin:connectors.calls.hint")}</CardDescription></div>
          <div className="flex items-center gap-3 text-xs">
            <label className="flex items-center gap-1.5"><Switch checked={failedOnly} onCheckedChange={setFailedOnly} /> {t("admin:integrations.failedOnly")}</label>
            <label className="flex items-center gap-1.5"><Switch checked={live} onCheckedChange={setLive} /> {t("admin:connectors.calls.live")}</label>
            <Button size="sm" variant="ghost" onClick={() => void calls.refetch()} loading={calls.isFetching}><RefreshCw /></Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {calls.isLoading ? <Skeleton className="h-64" /> : rows.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">{t("admin:connectors.calls.none")}</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-sand-600"><tr className="border-b border-sand-200 [&>th]:px-2 [&>th]:py-2 [&>th]:text-start"><th>{t("misc.time")}</th><th>{t("admin:integrations.operation")}</th><th>HTTP</th><th>{t("admin:integrations.latency")}</th><th>{t("misc.caseNumber")}</th><th>{t("admin:connectors.calls.purpose")}</th></tr></thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id} className={cn("border-b border-sand-100 align-top [&>td]:px-2 [&>td]:py-1.5", !c.succeeded && "bg-danger-50/40")}>
                    <td className="whitespace-nowrap text-muted-foreground">{formatDateTime(c.createdAt, locale)}</td>
                    <td><p className="font-medium">{c.operation ?? "—"}</p><p className="break-all font-mono text-[11px] text-muted-foreground" dir="ltr">{c.method} {c.path}</p>{c.error ? <p className="text-danger-700">{c.error}</p> : null}</td>
                    <td><Badge variant={c.succeeded ? "success" : "danger"} size="sm">{c.statusCode || "—"}</Badge></td>
                    <td className="tabular">{c.latencyMs} ms</td>
                    <td className="font-mono">{c.caseNumber ?? "—"}</td>
                    <td className="max-w-[260px] truncate text-muted-foreground" title={c.requestSummary ?? undefined}>{c.requestSummary ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
